import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

export interface Store {
  id: string;
  site_name: string;
  site_url: string;
  status: "pending" | "connected" | "syncing" | "disconnected" | "error";
  last_sync_at?: string;
  product_count: number;
  health_score: number;
  error_message?: string;
  currency?: string;
}

interface StoreContextType {
  stores: Store[];
  currentStore: Store | null;
  loading: boolean;
  setCurrentStore: (store: Store | null) => void;
  addStore: (store: Store) => Promise<void>;
  removeStore: (storeId: string) => Promise<void>;
  refreshStores: () => Promise<void>;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

const CURRENT_STORE_KEY = "proteasai_current_store_id";

export function StoreProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStores = async () => {
    if (!user) {
      setStores([]);
      setCurrentStore(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('stores')
        .select('id, site_name, site_url, status, last_sync_at, product_count, health_score, error_message, currency')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mappedStores: Store[] = (data || []).map(store => ({
        id: store.id,
        site_name: store.site_name,
        site_url: store.site_url,
        status: store.status as Store['status'],
        last_sync_at: store.last_sync_at || undefined,
        product_count: store.product_count || 0,
        health_score: store.health_score || 0,
        error_message: store.error_message || undefined,
        currency: store.currency || 'USD',
      }));

      setStores(mappedStores);

      if (mappedStores.length > 0) {
        // Try to restore from localStorage first
        const savedStoreId = localStorage.getItem(CURRENT_STORE_KEY);
        let storeToSelect: Store | null = null;

        if (savedStoreId) {
          // Try to find the saved store
          storeToSelect = mappedStores.find(s => s.id === savedStoreId) || null;
        }

        if (!storeToSelect && currentStore) {
          // Fallback: Update current store with fresh data
          storeToSelect = mappedStores.find(s => s.id === currentStore.id) || null;
        }

        if (!storeToSelect) {
          // Final fallback: Use first store
          storeToSelect = mappedStores[0];
        }

        setCurrentStore(storeToSelect);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stores when user is authenticated
  useEffect(() => {
    fetchStores();
  }, [user]);

  const addStore = async (store: Store) => {
    // Note: Actual store creation is handled by the store-upsert edge function
    // This method is for updating local state after successful creation
    setStores(prev => [store, ...prev]);
    if (!currentStore) {
      setCurrentStore(store);
    }
    toast.success('Store connected successfully');
  };

  const removeStore = async (storeId: string) => {
    try {
      const { error } = await supabase
        .from('stores')
        .delete()
        .eq('id', storeId);

      if (error) throw error;

      const updatedStores = stores.filter(store => store.id !== storeId);
      setStores(updatedStores);
      
      if (currentStore?.id === storeId) {
        setCurrentStore(updatedStores[0] || null);
      }
      toast.success('Store disconnected');
    } catch (error) {
      console.error('Error removing store:', error);
      toast.error('Failed to disconnect store');
    }
  };

  const refreshStores = async () => {
    await fetchStores();
  };

  const handleSetCurrentStore = (store: Store | null) => {
    setCurrentStore(store);
    if (store) {
      localStorage.setItem(CURRENT_STORE_KEY, store.id);
    } else {
      localStorage.removeItem(CURRENT_STORE_KEY);
    }
  };

  return (
    <StoreContext.Provider value={{ stores, currentStore, loading, setCurrentStore: handleSetCurrentStore, addStore, removeStore, refreshStores }}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
}
